import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  DataResponse,
  RecordsResponse,
} from 'src/app/modules/shared/models/models';
import { environment as config } from '../../../../environments/environment';
import { Observable, lastValueFrom, map } from 'rxjs';
import { Params } from '@angular/router';
import {
  IIndustriesListItem,
  IAbmCampaign,
  IAbmCampaignCompany,
  PagesVisitedResponse,
  IAbmCompanySource,
  IAbmCompanyGoal,
  IAbmCompanyInfo,
  IAbmCompanyVisitor,
  IAbmCompanyCampaign,
  IAbmCompanyStats,
  IAbmStats,
  IAbmGraphItem,
  IAbmImpact,
  IAbmCompany,
  ICrmDeal,
  IPageCompany,
  CompanyLifetime,
  AbmListRequest,
  AbmFlowStagesFromEtlRequest,
  AbmSourcesListRequest,
  IAbmSourceData,
} from './models';
import { BaseResponseData } from '../../shared/models/base-response';
import { Utils } from '../../shared/classes/utils';
import { DashboardContent } from '../dashboard/dashboard.model';

@Injectable({
  providedIn: 'root',
})
export class AbmService {
  private http = inject(HttpClient);

  companyLifetime(cleanCompanyName: string): Promise<CompanyLifetime> {
    const params: Params = { company: cleanCompanyName };
    return lastValueFrom(
      this.http.get<CompanyLifetime>(
        `${config.insightsBaseUrl}/api/abm/company/lifetime`,
        { params }
      )
    );
  }

  public async getCompaniesContent(
    request: AbmListRequest
  ): Promise<RecordsResponse<DashboardContent>> {
    const requestWithoutUndefined =
      Utils.getNonNullableQueryParameters(request);
    const params: Params = { ...requestWithoutUndefined };
    return lastValueFrom(
      this.http.get<RecordsResponse<DashboardContent>>(
        `${config.insightsBaseUrl}/api/abm/companies/content`,
        { params }
      )
    );
  }

  public async getCompaniesChannels(
    request: AbmSourcesListRequest
  ): Promise<RecordsResponse<IAbmSourceData>> {
    const requestWithoutUndefined =
      Utils.getNonNullableQueryParameters(request);
    const params: Params = { ...requestWithoutUndefined };
    return lastValueFrom(
      this.http.get<RecordsResponse<IAbmSourceData>>(
        `${config.insightsBaseUrl}/api/abm/companies/sources`,
        { params }
      )
    );
  }

  companies(filters: Params): Observable<RecordsResponse<IAbmCompany>> {
    return this.http.get<RecordsResponse<IAbmCompany>>(
      `${config.insightsBaseUrl}/api/abm/companies`,
      {
        params: filters,
      }
    );
  }
  public async getCompanies(
    request: AbmListRequest
  ): Promise<RecordsResponse<IAbmCompany>> {
    const requestWithoutUndefined =
      Utils.getNonNullableQueryParameters(request);
    const params: Params = { ...requestWithoutUndefined };
    return lastValueFrom(
      this.http.get<RecordsResponse<IAbmCompany>>(
        `${config.insightsBaseUrl}/api/abm/companies`,
        { params }
      )
    );
  }
  public async getCompaniesImpact(
    request: AbmFlowStagesFromEtlRequest
  ): Promise<IAbmImpact> {
    const params: Params = { ...request };
    return lastValueFrom(
      this.http
        .get<{ data: IAbmImpact }>(
          `${config.insightsBaseUrl}/api/abm/companies/impact`,
          { params }
        )
        .pipe(map((res) => res.data))
    );
  }

  industries(
    filters: Params
  ): Observable<RecordsResponse<IIndustriesListItem>> {
    return this.http.get<RecordsResponse<IIndustriesListItem>>(
      `${config.insightsBaseUrl}/api/abm/industries`,
      {
        params: filters,
      }
    );
  }

  campaigns(filters: Params): Observable<RecordsResponse<IAbmCampaign>> {
    return this.http.get<RecordsResponse<IAbmCampaign>>(
      `${config.insightsBaseUrl}/api/abm/campaigns`,
      {
        params: filters,
      }
    );
  }

  campaignDetails(
    filters: Params
  ): Observable<{ data: IAbmCampaignCompany[] }> {
    return this.http.get<{ data: IAbmCampaignCompany[] }>(
      `${config.insightsBaseUrl}/api/abm/campaign_details`,
      {
        params: filters,
      }
    );
  }

  companyStats(filters: Params): Observable<DataResponse<IAbmCompanyStats>> {
    return this.http.get<DataResponse<IAbmCompanyStats>>(
      `${config.insightsBaseUrl}/api/abm/company/stats`,
      {
        params: filters,
      }
    );
  }

  companyDailyPageviews(
    filters: Params
  ): Observable<{ data: [number, number][] }> {
    return this.http.get<{ data: [number, number][] }>(
      `${config.insightsBaseUrl}/api/abm/company/daily_page_views`,
      {
        params: filters,
      }
    );
  }

  companyPageVisits(filters: Params): Observable<PagesVisitedResponse> {
    return this.http.get<PagesVisitedResponse>(
      `${config.insightsBaseUrl}/api/abm/company/pages_visited`,
      {
        params: filters,
      }
    );
  }

  companySources(
    filters: Params
  ): Observable<RecordsResponse<IAbmCompanySource>> {
    return this.http.get<RecordsResponse<IAbmCompanySource>>(
      `${config.insightsBaseUrl}/api/abm/company/sources`,
      {
        params: filters,
      }
    );
  }

  companyGoals(filters: Params): Observable<RecordsResponse<IAbmCompanyGoal>> {
    return this.http.get<RecordsResponse<IAbmCompanyGoal>>(
      `${config.insightsBaseUrl}/api/abm/company/goals`,
      {
        params: filters,
      }
    );
  }

  companyInfo(
    companyName: string,
    cleanCompanyName: string,
    fromDate: string
  ): Observable<DataResponse<IAbmCompanyInfo>> {
    return this.http.get<DataResponse<IAbmCompanyInfo>>(
      `${config.insightsBaseUrl}/api/abm/company/info`,
      {
        params: {
          company: companyName,
          cleanCompany: cleanCompanyName,
          fromDate,
        },
      }
    );
  }

  companyDeals(filters: Params): Observable<RecordsResponse<ICrmDeal>> {
    return this.http.get<RecordsResponse<ICrmDeal>>(
      `${config.insightsBaseUrl}/api/abm/company/deals`,
      {
        params: filters,
      }
    );
  }

  impactedDeals(filters: Params): Observable<RecordsResponse<ICrmDeal>> {
    return this.http.get<RecordsResponse<ICrmDeal>>(
      `${config.insightsBaseUrl}/api/abm/deals`,
      {
        params: filters,
      }
    );
  }

  pageCompanies(filters: Params): Observable<RecordsResponse<IPageCompany>> {
    return this.http.get<RecordsResponse<IPageCompany>>(
      `${config.insightsBaseUrl}/api/abm/page-companies`,
      {
        params: filters,
      }
    );
  }

  dealStages(): Observable<string[]> {
    return this.http.get<string[]>(
      `${config.insightsBaseUrl}/api/abm/deal_stages`
    );
  }

  dealsValue(filters: Params): Observable<number> {
    return this.http.get<number>(
      `${config.insightsBaseUrl}/api/abm/deals_value`,
      {
        params: filters,
      }
    );
  }

  companyVisitors(
    filters: Params
  ): Observable<RecordsResponse<IAbmCompanyVisitor>> {
    return this.http.get<RecordsResponse<IAbmCompanyVisitor>>(
      `${config.insightsBaseUrl}/api/abm/company/visitors`,
      {
        params: filters,
      }
    );
  }

  companyCampaigns(
    filters: Params
  ): Observable<DataResponse<IAbmCompanyCampaign[]>> {
    return this.http.get<DataResponse<IAbmCompanyCampaign[]>>(
      `${config.insightsBaseUrl}/api/abm/company/campaigns`,
      {
        params: filters,
      }
    );
  }

  stats(filters: Params): Observable<DataResponse<IAbmStats>> {
    return this.http.get<DataResponse<IAbmStats>>(
      `${config.insightsBaseUrl}/api/abm/companies_stats`,
      {
        params: filters,
      }
    );
  }

  getGraphData(filters: Params): Observable<DataResponse<IAbmGraphItem[]>> {
    return this.http.get<DataResponse<IAbmGraphItem[]>>(
      `${config.insightsBaseUrl}/api/abm/companies/graph`,
      { params: filters }
    );
  }

  getAbmImpact(filters: Params): Observable<DataResponse<IAbmImpact>> {
    return this.http.get<DataResponse<IAbmImpact>>(
      `${config.insightsBaseUrl}/api/abm/companies/impact_fast`,
      { params: filters }
    );
  }

  getCleanCompany(filters: Params): Observable<DataResponse<string>> {
    return this.http.get<DataResponse<string>>(
      `${config.insightsBaseUrl}/api/abm/company/clean_company`,
      {
        params: filters,
      }
    );
  }

  getAnalyzedCleanCompany(companyName: string): Promise<string> {
    const params = { name: companyName };
    return lastValueFrom(
      this.http
        .get<BaseResponseData<string>>(`${config.abmBaseUrl}/company/analyze`, {
          params,
        })
        .pipe(map((res) => res.data))
    );
  }
}
